import { useContext } from 'react'

import type {
  GoogleSearchMethods,
  Suggestion as PlacesApiSuggestion,
} from '@awazevr/mfe-common.api.places-api'
import {
  PlacesContext,
  useSuggestions,
} from '@awazevr/mfe-common.api.places-api'
import { formatPropertyPageURL } from '@awazevr/mfe-common.utils.pdp-url'

import { formatAutocompleteSubtitle } from '@/components/SearchForm/SearchForm.helper'
import {
  PlacesSuggestions,
  Suggestion,
} from '@/components/SearchForm/SearchForm.types'
import { Assert } from '@/utils/assert'
import { dataLayerErrorHandler } from '@/utils/dataLayerErrorHandler'

const mapPropertySuggestion = (
  item: PlacesApiSuggestion,
  pdpUrlSegment: string,
): Suggestion => ({
  ...item,
  link: formatPropertyPageURL(
    pdpUrlSegment,
    item.name,
    item.code ?? '',
    undefined,
    item.siteName,
    item.siteCode,
  ),
  subtitle: [item.code, item.location].filter(Boolean).join(', '),
})

const mapLocationSuggestion = (item: PlacesApiSuggestion): Suggestion => ({
  ...item,
  subtitle: formatAutocompleteSubtitle(item),
  link: '',
})

export const usePlacesSuggestions = (
  googleSearchMethod: GoogleSearchMethods,
  getPdpUrlSegment: (accommodationType?: string) => string,
): PlacesSuggestions => {
  const getSuggestions = useSuggestions()
  const { useSuggestionsRestEndpoint } = useContext(PlacesContext)

  return async (searchTerm: string, accommodationType?: string) => {
    try {
      const suggestions = await getSuggestions(
        searchTerm,
        100,
        googleSearchMethod,
        accommodationType,
      )
      const pdpUrlSegment = getPdpUrlSegment(accommodationType)
      return suggestions.map((item) =>
        item.property
          ? mapPropertySuggestion(item, pdpUrlSegment)
          : mapLocationSuggestion(item),
      )
    } catch (e) {
      Assert.error(e)
      // eslint-disable-next-line no-console
      console.log('Error while calling places', e.message)
      dataLayerErrorHandler(
        e,
        'usePlacesSuggestions: failed to fetch suggestions',
        {
          googleSearchMethod,
          searchTermLength: searchTerm.length,
          useSuggestionsRestEndpoint,
        },
      )
      return []
    }
  }
}
